import type { ViewWidgetSetting, ViewWidgetSettingsItem } from 'types';
import { createPosString, getRange } from 'utils/section/gridUtils';

export interface WidgetGridSize {
  gridSizeX: number;
  gridSizeY: number;
}

export interface WidgetGridCell {
  x: string;
  y: string;
  identifier: string;
  isEmpty: boolean;
  data: ViewWidgetSetting | null;
  id?: string;
}

export const normalizeWidgetGridCoordinate = (value: number | undefined, fallback: number) => {
  if (!value || value < 1) return fallback;

  return value;
};

export const normalizeWidgetGridSpan = (value: number | undefined) => {
  if (!value || value < 1) return 1;

  return value;
};

export const getViewWidgetGrid = (item: ViewWidgetSettingsItem, fallback: { row: number; column: number }) => ({
  row: normalizeWidgetGridCoordinate(item.row, fallback.row),
  column: normalizeWidgetGridCoordinate(item.column, fallback.column),
  rowSpan: normalizeWidgetGridSpan(item.rowSpan),
  columnSpan: normalizeWidgetGridSpan(item.columnSpan),
});

export const createWidgetGridCells = (items: ViewWidgetSetting[], gridSize: WidgetGridSize) => {
  const itemMap = new Map<string, ViewWidgetSetting>();

  items.forEach((item) => {
    itemMap.set(`${item.column},${item.row}`, item);
  });

  return Array.from({ length: gridSize.gridSizeX * gridSize.gridSizeY }, (_, index) => {
    const column = (index % gridSize.gridSizeX) + 1;
    const row = Math.floor(index / gridSize.gridSizeX) + 1;
    const item = itemMap.get(`${column},${row}`);

    if (item) {
      return {
        x: createPosString(item.column, item.columnSpan),
        y: createPosString(item.row, item.rowSpan),
        identifier: item.formItemId,
        isEmpty: false,
        data: item,
        id: item.formItemId,
      };
    }

    return {
      x: String(column),
      y: String(row),
      identifier: `empty-${column}-${row}`,
      isEmpty: true,
      data: null,
    };
  });
};

export const getWidgetGridSize = (items: ViewWidgetSetting[]) => {
  const maxColumn = items.reduce((max, item) => Math.max(max, item.column + item.columnSpan - 1), 1);
  const maxRow = items.reduce((max, item) => Math.max(max, item.row + item.rowSpan - 1), 1);

  return {
    gridSizeX: Math.max(1, maxColumn),
    gridSizeY: Math.max(1, maxRow),
  };
};

export const toLocalFormItem = (item: WidgetGridCell) => ({
  x: item.x,
  y: item.y,
  identifier: item.identifier,
  isEmpty: item.isEmpty,
  data: null,
  id: item.id,
});

export const getPositionSpan = (item: ViewWidgetSetting, position: 'row' | 'column') => (position === 'row' ? item.rowSpan : item.columnSpan);

export const getOccupiedIndices = (items: ViewWidgetSetting[], position: 'row' | 'column') =>
  new Set(
    items.flatMap((item) => {
      const start = item[position];
      const span = getPositionSpan(item, position);

      return Array.from({ length: span }, (_, index) => start + index);
    })
  );

export const parsePosition = (value: string) => {
  const { start, end } = getRange(value);

  return {
    start,
    span: end - start + 1,
  };
};

export const updatePosition = (item: ViewWidgetSetting, position: 'row' | 'column', value: string) => {
  const shifted = parsePosition(value);

  return position === 'column'
    ? { ...item, column: shifted.start, columnSpan: shifted.span }
    : { ...item, row: shifted.start, rowSpan: shifted.span };
};
